import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to auto-hide navigation bars on scroll in landscape mode
 * Only activates when screen width < 1024px AND height < 500px (landscape)
 * 
 * @returns {boolean} isVisible - Whether the navigation bars should be visible
 */
export const useAutoHideOnScroll = (): boolean => {
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    // Check if we're in landscape mode
    const isLandscape = () => {
      return window.innerWidth < 1024 && window.innerHeight < 500;
    };

    // Handle scroll event
    const handleScroll = () => {
      // Only apply auto-hide in landscape mode
      if (!isLandscape()) {
        setIsVisible(true);
        return;
      }

      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY || window.pageYOffset;
          
          // Scrolling down - hide bars
          if (currentScrollY > lastScrollY.current && currentScrollY > 50) {
            setIsVisible(false);
          }
          // Scrolling up - show bars
          else if (currentScrollY < lastScrollY.current) {
            setIsVisible(true);
          }
          // At top of page - always show bars
          else if (currentScrollY < 10) {
            setIsVisible(true);
          }

          lastScrollY.current = currentScrollY;
          ticking.current = false;
        });

        ticking.current = true;
      }
    };

    // Handle touch events (for mobile)
    const handleTouchStart = () => {
      if (isLandscape()) {
        setIsVisible(true);
      }
    };

    // Handle orientation change
    const handleOrientationChange = () => {
      // Always show bars when orientation changes
      setIsVisible(true);
      lastScrollY.current = 0;
    };

    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  return isVisible;
};
